import { useMemo, useState } from 'react';

import { useAuth } from 'contexts';
import { Role } from 'types';
import ChartCard from './chart-card';
import { CustomerUnits, DateTransactions, SizeTypeUnits, StatusUnits } from './charts';
import Inventory from './charts/inventory';
import { SizeTypeDataProvider } from './charts/size-type-units';
import ZoomableTreemap from './charts/zoomable-treemap';
import './dashboard.scss';

const charts = {
  'TRANSACTIONS': DateTransactions,
  'INVENTORY': Inventory,
  'CUSTOMERS': CustomerUnits,
  'ACTIVE UNITS': ZoomableTreemap,
  'SIZE TYPES': SizeTypeUnits,
  'CHASSIS': SizeTypeUnits,
  'CONTAINERS (DRY)': SizeTypeUnits,
  'REEFERS': SizeTypeUnits,
  'GENSETS': SizeTypeUnits,
  'STATUSES': StatusUnits
};

const Dashboard = () => {
  const { userProfile } = useAuth();

  const [focused, setFocused] = useState<keyof typeof charts>();
  const [splitscreen, setSplitscreen] = useState<boolean>(
    ![Role.ADMIN_CUSTOMER, Role.CUSTOMER, Role.EXTERNAL, Role.CARRIER].includes(userProfile.role)
  );

  const displayCustomerGraph = useMemo(() => {
    return (
      userProfile.role === Role.ADMIN ||
      userProfile.role === Role.BILLING_ADMIN ||
      userProfile.role === Role.SENIOR_BOOKING ||
      userProfile.role === Role.BOOKING ||
      ((userProfile.role === Role.ADMIN_CUSTOMER ||
        userProfile.role === Role.CUSTOMER ||
        userProfile.role === Role.EXTERNAL) &&
        (userProfile.customers?.length ?? 0) > 1)
    );
  }, [userProfile.customers, userProfile.role]);

  return (
    <div className="page overflow-hidden" id="dashboard">
      <div className="page-header">
        <h1>DASHBOARD</h1>
        {focused ? null : (
          <div className="grid-3 h-100">
            <button className={!splitscreen ? 'btn-primary' : 'btn-outline'} onClick={() => setSplitscreen(false)}>
              <span className="material-icons icon-large">horizontal_split</span>
            </button>
            <button className={splitscreen ? 'btn-primary' : 'btn-outline'} onClick={() => setSplitscreen(true)}>
              <span className="material-icons icon-large">vertical_split</span>
            </button>
          </div>
        )}
      </div>
      <div className="flex-fill overflow-auto">
        <SizeTypeDataProvider>
          <div
            className={
              focused ? 'grid-1 h-100 w-100 p-1' : `grid-big-3 auto-rows h-100 p-1 customers-${displayCustomerGraph}`
            }
          >
            <ChartCard
              className={splitscreen ? 'col-span-2' : 'col-span-3'}
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CARRIER
              CUSTOMER
              EXTERNAL
              Chart={charts.TRANSACTIONS}
              focused={focused}
              setFocused={setFocused}
              title="TRANSACTIONS"
            />
            <ChartCard
              className={splitscreen ? 'col-span-2' : 'col-span-3'}
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts.INVENTORY}
              focused={focused}
              setFocused={setFocused}
              title="INVENTORY"
            />
            <ChartCard
              className={splitscreen ? 'col-span-2' : ''}
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER={(userProfile.customers?.length ?? 0) > 1}
              SENIOR_BOOKING
              BOOKING
              CUSTOMER={(userProfile.customers?.length ?? 0) > 1}
              EXTERNAL={(userProfile.customers?.length ?? 0) > 1}
              Chart={charts.CUSTOMERS}
              focused={focused}
              setFocused={setFocused}
              title="CUSTOMERS"
            />
            <ChartCard
              className={
                splitscreen
                  ? `col-start-3 row-start-1 row-end-${3 + Number(displayCustomerGraph)}`
                  : `col-start-3 row-start-3 row-end-${6 - Number(displayCustomerGraph)}`
              }
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts['ACTIVE UNITS']}
              focused={focused}
              setFocused={setFocused}
              title="ACTIVE UNITS"
            />
            <ChartCard
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts.STATUSES}
              focused={focused}
              setFocused={setFocused}
              title="STATUSES"
            />
            <ChartCard
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts['SIZE TYPES']}
              focused={focused}
              setFocused={setFocused}
              title="SIZE TYPES"
            />
            <ChartCard
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts.CHASSIS}
              focused={focused}
              setFocused={setFocused}
              title="CHASSIS"
              filter={data => data.category === 'CHASSIS'}
            />
            <ChartCard
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts['CONTAINERS (DRY)']}
              focused={focused}
              setFocused={setFocused}
              title="CONTAINERS (DRY)"
              filter={data => data.category === 'DRY'}
            />
            <ChartCard
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts.REEFERS}
              focused={focused}
              setFocused={setFocused}
              filter={data => data.category === 'REEFER'}
              title="REEFERS"
            />
            <ChartCard
              ADMIN
              BILLING_ADMIN
              ADMIN_CUSTOMER
              SENIOR_BOOKING
              BOOKING
              CUSTOMER
              EXTERNAL
              Chart={charts.GENSETS}
              focused={focused}
              setFocused={setFocused}
              filter={data => data.category === 'GENSET'}
              title="GENSETS"
            />
          </div>
        </SizeTypeDataProvider>
      </div>
    </div>
  );
};

export default Dashboard;
